/**
 * Tamper-evident Decision Ledger — the trust moat.
 *
 * Every APPROVED agent action (and every undo/reversal of one) is written
 * here as an append-only, SHA-256 hash-chained entry. Each entry's `hash`
 * digests the previous entry's `hash` plus a canonical serialization of the
 * entry itself, so the whole history is verifiable in one pass: change,
 * delete, or reorder any single entry and {@link verifyChain} pinpoints the
 * exact `seq` where the chain breaks.
 *
 * This is a first-class PERSISTED ledger backed by its own `ledger` store
 * collection (registered in both store backends), distinct from the
 * activity-log-derived FNV chain in `src/lib/ledger/verifyChain.ts`. It
 * OBSERVES the approval pipeline — it never gates it. Appends are async
 * (crypto.subtle is async) and best-effort: a ledger failure must never
 * break the action it records.
 */

import { db, nowIso } from '../db/store';

// --- Types -------------------------------------------------------------------

/** What produced this entry: a forward action, or the undo of one. */
export type LedgerKind = 'action' | 'reversal';

/**
 * One link in the tamper-evident chain. Persisted in the `ledger`
 * collection. `hash` is computed over everything EXCEPT `hash` itself
 * (see {@link computeHash}), chained to `prevHash`.
 */
export interface DecisionLedgerEntry {
  /** Firestore doc id (== `ledger-<seq>`), so the store can key it. */
  id: string;
  /** Monotonic position in the chain, starting at 0 (genesis). */
  seq: number;
  /** ISO-8601 UTC timestamp the entry was sealed. */
  ts: string;
  /** Who approved / triggered the action ('operator' when unknown). */
  actor: string;
  /** Forward action or its reversal. */
  kind: LedgerKind;
  /** The AgentAction discriminant, e.g. 'create_task'. */
  actionType: string;
  /** Human-readable one-liner (verb — detail). */
  summary: string;
  /** SHA-256 hex of the action payload — proves WHAT was decided. */
  payloadHash: string;
  /** The previous entry's `hash`; 'genesis' for seq 0. */
  prevHash: string;
  /** SHA-256 hex of (prevHash + '|' + canonicalJson(entryWithoutHash)). */
  hash: string;
}

/** The genesis anchor — the prevHash of the very first entry. */
export const GENESIS_PREV_HASH = 'genesis';

/** Result of a full-chain verification. */
export type VerifyResult =
  | { ok: true }
  | { ok: false; brokenAtSeq: number };

/** Everything an appender must supply; the chain fields are derived. */
export interface LedgerInput {
  actor: string;
  kind: LedgerKind;
  actionType: string;
  summary: string;
  /** Serializable action payload; hashed into `payloadHash`. */
  payload: unknown;
}

// --- Canonical serialization -------------------------------------------------

/**
 * Deterministic JSON: object keys sorted recursively so semantically equal
 * values always serialize to the same string (hashing is order-invariant on
 * keys). Arrays keep their order (order is meaningful). `undefined` values
 * are dropped, matching JSON.stringify semantics. Pure.
 */
export function canonicalJson(value: unknown): string {
  return serialize(value);
}

function serialize(value: unknown): string {
  if (value === null) return 'null';
  const t = typeof value;
  if (t === 'number') return Number.isFinite(value as number) ? String(value) : 'null';
  if (t === 'boolean') return value ? 'true' : 'false';
  if (t === 'string') return JSON.stringify(value);
  if (t === 'bigint') return JSON.stringify((value as bigint).toString());
  if (Array.isArray(value)) {
    return '[' + value.map((v) => (v === undefined ? 'null' : serialize(v))).join(',') + ']';
  }
  if (t === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined && typeof obj[k] !== 'function')
      .sort();
    return (
      '{' +
      keys.map((k) => JSON.stringify(k) + ':' + serialize(obj[k])).join(',') +
      '}'
    );
  }
  // undefined / function at the top level → JSON has no representation.
  return 'null';
}

// --- SHA-256 -----------------------------------------------------------------

/**
 * Resolve a SubtleCrypto once. In the browser it's `crypto.subtle`; under
 * Vitest/jsdom Node exposes the same API via `crypto.webcrypto.subtle`.
 * Cached so we resolve the implementation exactly once.
 */
let subtlePromise: Promise<SubtleCrypto> | null = null;

async function getSubtle(): Promise<SubtleCrypto> {
  if (!subtlePromise) {
    subtlePromise = (async () => {
      const g = globalThis as { crypto?: Crypto };
      if (g.crypto?.subtle) return g.crypto.subtle;
      // Node (tests) — webcrypto lives on the crypto module.
      const nodeCrypto = (await import('node:crypto')) as unknown as {
        webcrypto?: Crypto;
      };
      if (nodeCrypto.webcrypto?.subtle) return nodeCrypto.webcrypto.subtle;
      throw new Error('[ledger] no SubtleCrypto implementation available');
    })();
  }
  return subtlePromise;
}

/** SHA-256 of a UTF-8 string, returned as lowercase hex. Async, isomorphic. */
export async function sha256Hex(input: string): Promise<string> {
  const subtle = await getSubtle();
  const bytes = new TextEncoder().encode(input);
  const digest = await subtle.digest('SHA-256', bytes);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) {
    hex += view[i].toString(16).padStart(2, '0');
  }
  return hex;
}

// --- Hash computation --------------------------------------------------------

/** The chain-covered fields of an entry (everything except `hash`). */
type EntryWithoutHash = Omit<DecisionLedgerEntry, 'hash'>;

/**
 * The canonical hash for an entry: SHA-256 of
 * `prevHash + '|' + canonicalJson(entryWithoutHash)`. Deterministic and
 * isomorphic. Exported so the UI verifier and the appender share one path.
 */
export async function computeHash(entry: EntryWithoutHash): Promise<string> {
  return sha256Hex(entry.prevHash + '|' + canonicalJson(entry));
}

// --- Persistence -------------------------------------------------------------

/** Current entries, sorted by seq ascending (oldest → newest). */
export async function listLedger(): Promise<DecisionLedgerEntry[]> {
  const all = await db.list('ledger');
  return [...all].sort((a, b) => a.seq - b.seq);
}

/**
 * Append a new entry to the tail of the chain. Reads the current head to
 * derive `seq` + `prevHash`, computes `payloadHash` and `hash`, and persists.
 * Returns the sealed entry. Callers that want resilience should use
 * {@link appendLedgerSafe}, which swallows failures.
 */
export async function appendLedgerEntry(input: LedgerInput): Promise<DecisionLedgerEntry> {
  const existing = await listLedger();
  const head = existing[existing.length - 1];
  const seq = head ? head.seq + 1 : 0;
  const prevHash = head ? head.hash : GENESIS_PREV_HASH;

  const payloadHash = await sha256Hex(canonicalJson(input.payload));
  const withoutHash: EntryWithoutHash = {
    id: `ledger-${seq}`,
    seq,
    ts: nowIso(),
    actor: input.actor,
    kind: input.kind,
    actionType: input.actionType,
    summary: input.summary,
    payloadHash,
    prevHash,
  };
  const hash = await computeHash(withoutHash);
  const entry: DecisionLedgerEntry = { ...withoutHash, hash };
  await db.add('ledger', entry);
  return entry;
}

/**
 * Resilient append: never throws. A ledger failure must NOT break the action
 * that triggered it — we log and continue. Returns the entry on success, or
 * null if the append failed.
 */
export async function appendLedgerSafe(input: LedgerInput): Promise<DecisionLedgerEntry | null> {
  try {
    return await appendLedgerEntry(input);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[ledger] append failed (action unaffected):', err);
    return null;
  }
}

// --- Verification ------------------------------------------------------------

/**
 * Recompute every hash in seq order and confirm the chain is intact:
 *   • seq values are contiguous from 0,
 *   • each `prevHash` equals the prior entry's `hash` (genesis = GENESIS_PREV_HASH),
 *   • each `hash` equals a fresh {@link computeHash} of its own fields.
 * Returns { ok: true } for an intact (or empty) chain, else
 * { ok: false, brokenAtSeq } for the FIRST anomaly. Order-independent input:
 * entries are sorted by seq first.
 */
export async function verifyChain(entries: DecisionLedgerEntry[]): Promise<VerifyResult> {
  const sorted = [...entries].sort((a, b) => a.seq - b.seq);
  let prevHash = GENESIS_PREV_HASH;
  let expectedSeq = 0;

  for (const e of sorted) {
    if (e.seq !== expectedSeq) return { ok: false, brokenAtSeq: e.seq };
    if (e.prevHash !== prevHash) return { ok: false, brokenAtSeq: e.seq };
    const { hash: _stored, ...withoutHash } = e;
    void _stored;
    const recomputed = await computeHash(withoutHash);
    if (recomputed !== e.hash) return { ok: false, brokenAtSeq: e.seq };
    prevHash = e.hash;
    expectedSeq = e.seq + 1;
  }

  return { ok: true };
}
